"use client";

import { useCallback, useMemo, useRef } from "react";
import type { NavigationAdapter } from "../context/execution-context";
import { executeFlow } from "../runtime/execute-flow";
import { createBrowserAuthAdapter, createBrowserStorageAdapter } from "./runtime-adapters";
import { createFetchCallTransport } from "../createFetchCallTransport";
import { createFetchStreamTransport } from "../createFetchStreamTransport";
import { resolveFlowFromApp } from "./StudioScreenRenderer.flow.raw";
import type {
  RunScreenFlow, ScreenExecutionDeps, ScreenExecutionInputs,
} from "./StudioScreenRenderer.execution-types";

/**
 * The runtime auth adapter must target the same localStorage key the app's
 * flows use for their canonical session blob (`<appSlug>.session`, e.g.
 * "mental2.session"). Otherwise credentials written by auth:write and the
 * session blob written by storage:write diverge, and screens whose mount
 * bootstrap reads the adapter see no credentials and bounce to the auth
 * screen. The legacy "studio.auth" key is only used when the app has no slug.
 */
function resolveAuthStorageKey(appSlug: string): string {
  const slug = appSlug.trim();
  return slug ? `${slug}.session` : "studio.auth";
}

/**
 * SSR-safe browser defaults. Both adapters no-op when `window` is
 * unavailable, so they can be created unconditionally on the client renderer.
 *
 * Without these defaults, `ctx.auth`/`ctx.storage` were `null` whenever the
 * host app did not pass the adapter props, which silently skipped the
 * `auth:write` and `storage:write` steps in auth flows — credentials never
 * reached localStorage after a successful login.
 */
export function useScreenExecution(inputs: ScreenExecutionInputs) {
  const { app, store, route, screen, formRef, navigate, storage: storageProp, auth: authProp,
    logger, operationsBaseUrl } = inputs;
  const browserStorage = useMemo(() => createBrowserStorageAdapter(), []);
  const authStorageKey = resolveAuthStorageKey(app.slug ?? "");
  const browserAuth = useMemo(
    () => createBrowserAuthAdapter({ storageKey: authStorageKey }),
    [authStorageKey],
  );
  const storage = storageProp ?? browserStorage;
  const auth = authProp ?? browserAuth;
  const transport = useMemo(
    () => createFetchCallTransport({ baseUrl: operationsBaseUrl }),
    [operationsBaseUrl],
  );
  const streamTransport = useMemo(
    () => createFetchStreamTransport({ baseUrl: operationsBaseUrl }),
    [operationsBaseUrl],
  );
  const navigationAdapter = useMemo<NavigationAdapter | null>(
    () => navigate ? { navigate } : null,
    [navigate],
  );

  // Read the latest dependencies when a stable trigger callback runs.
  const executionDeps = useRef<ScreenExecutionDeps>({
    app, store, transport, streamTransport, navigationAdapter,
    storage, auth, logger, route, screen,
  });
  executionDeps.current = {
    app, store, transport, streamTransport, navigationAdapter,
    storage, auth, logger, route, screen,
  };

  const runFlow = useCallback<RunScreenFlow>(async (flow, args) => {
    const d = executionDeps.current;
    const session =
      (d.store.get("session") as Record<string, unknown> | undefined) ?? {};
    return executeFlow(flow, {
      args,
      // Atom triggers fire flows with the interaction payload (eventPayload,
      // form values, control values). Flows author their branches against the
      // `@event` scope (e.g. `@event.session.sessionId`, `@event.kind`), so the
      // payload must also be exposed as `event` — passing it only as `args`
      // left every `@event.*` binding unresolved and silently disabled card
      // open/delete actions and feedback kind selection.
      event: args,
      initialForm: { ...formRef.current },
      state: d.store,
      storage: d.storage,
      auth: d.auth,
      navigation: d.navigationAdapter ?? { navigate: () => {} },
      transport: d.transport,
      streamTransport: d.streamTransport,
      logger: d.logger,
      route: d.route,
      session,
      resolveFlow: (id: string) => resolveFlowFromApp(d.app, id),
    });
  }, []);

  // The resolved adapters are exported so the mount bootstrap reads the same
  // credentials the flows write. Passing the raw `props.auth` to the bootstrap
  // instead used to read from an undefined adapter and bounce every
  // authGate-required screen back to login.
  return { transport, executionDeps, runFlow, storage, auth };
}